import { Component, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize, Observable } from 'rxjs';

import { PageSize } from 'src/app/enumeration/pageSize';
import { ProviderType } from 'src/app/enumeration/provider-type';
import { TransactionApiService } from 'src/app/service/transaction/transaction-api.service';
import { CheckRolesAccessService } from 'src/app/utils/check_roles_access/check-roles-access.service';
import { DateFormatPipe } from 'src/app/utils/date-format.pipe';

// Explicit type-safe interface schemas layout definitions

@Component({
  selector: 'app-redeem-transaction-list',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    DateFormatPipe,
  ],
  templateUrl: './redeem-transaction-list.component.html',
  styleUrl: './redeem-transaction-list.component.css',
})
export class RedeemTransactionListComponent implements OnInit {
  // Functional dependency injection primitives (Constructor-less pattern)
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(TransactionApiService);
  private readonly checkAccessRoles = inject(CheckRolesAccessService);
  private readonly destroyRef = inject(DestroyRef);

  // Fully refactored component view data tracking metrics utilizing signals
  readonly transactionList = signal<any[]>([]);
  readonly allTotal = signal<number>(0);
  readonly page = signal<number>(1); // Optimized 1-based index template pagination matching parameter
  readonly pageSize = signal<number>(PageSize.REDEEM_LISTS);
  readonly loading = signal<boolean>(false);

  // Active state context variables
  redeemId = '';
  redeemStatusType = '';
  type = '';
  mathMin = Math.min;

  ngOnInit(): void {
    this.observeRouteParameters();
  }

  /**
   * Subscribes securely to path navigation snapshots parameter matrix
   */
  private observeRouteParameters(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.redeemId = params.get('redeemId') || '';
        this.redeemStatusType = params.get('type') || '';

        // Replaced loose switch case conditional logic branches with an elegant mapping object
        const providerMapping: Record<string, string> = {
          [ProviderType.KBZ]: 'KBZ',
          [ProviderType.WAVE]: 'BOW',
          [ProviderType.AYA]: 'BOW',
          [ProviderType.Phone_Bill]: 'BOP',
        };

        this.type = providerMapping[this.redeemStatusType] || '';

        if (this.type) {
          this.fetchTransactionList();
        } else {
          console.error('Unhandled or unidentified provider type parameter token context detected:', this.redeemStatusType);
        }
      });
  }

  /**
   * Dispatches network lookup commands to stream active matching historical statements indices
   */
  fetchTransactionList(): void {
    this.loading.set(true);
    this.transactionList.set([]);

    // Resolved dynamic observable streaming dictionary mapping allocations
    const observableStrategyMap: Record<string, () => Observable<any[]>> = {
      'KBZ': () => this.apiService.getRedeemedTransactionKBZById(this.redeemId),
      'BOW': () => this.apiService.getRedeemedTransactionBOWById(this.redeemId),
      'BOP': () => this.apiService.getRedeemedTransactionBOPById(this.redeemId),
    };

    const targetObservableFactory = observableStrategyMap[this.type];

    if (!targetObservableFactory) {
      this.loading.set(false);
      console.error('No valid transaction list streaming pipeline strategy discovered for context:', this.type);
      return;
    }

    targetObservableFactory()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)) // Unconditionally turns off active background processing loaders
      )
      .subscribe({
        next: (transactions: any[]) => {
          this.transactionList.set(transactions || []);
          this.allTotal.set(transactions?.length || 0);
        },
        error: (err: any) => {
          console.error('Transaction records network lookup retrieval failure exception:', err);
          alert(err?.message || err || 'Network failure encountered while fetching transaction profiles.');
        }
      });
  }

  /**
   * Captures event page shifts step sequences securely across viewport boundaries
   */
  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event);
    }
  }

  goBack(): void {
    window.history.back();
  }
}
